import {
  DashboardState,
  ECardinality,
  ESymbolCategory,
  type IAction,
  type IActionDefinitionDto,
  type IActionParameter,
  type IDisplayItem,
  type IDisplayItemProperty,
  type IEvent
} from "@kuika/kuika-cl-model";
import type { CascaderOptionType } from "antd/es/cascader";
import type { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { getActionParameterDoc } from "../../../../../shared/utility/action-doc";
import type { ISymbolPickerCascaderProps } from "../../../../../shared/utility/designer-symbol-picker-engine";
import {
  resolveSymbolPickerActionResultOrigin,
  type ISymbolPickerActionResultOrigin,
  type ISymbolPickerActionSequence
} from "../../../../../shared/utility/symbol-picker-action-order";
import { displaySymbolPickerTypeName } from "../../../../../shared/utility/symbol-picker-type-compat";
import { getActionParameterLabel, type IActionParameterLabel } from "../action-parameter-label";
import type {
  ISymbolPickerBindingTarget,
  ISymbolPickerComponentFacts,
  ISymbolPickerPreviewContext
} from "./symbol-picker-preview-model";

/**
 * Builds the preview pane's host facts (ISymbolPickerPreviewContext) from what SymbolPickerCascader
 * already has — its props (`parameter`, `bindingParameter`, `bindingLabel`, `action`, `property`,
 * `targetDI`, `targetTypeName`, `targetCardinality`) and the screen design (`sd`). Nothing is
 * threaded from further up. `bindingParameter` is a preview-only fallback for hosts that edit a
 * named action parameter's symbol without wiring `parameter` itself (see SymbolPicker's doc).
 *
 * The screen walks are lazy and done once per context: the action lists on the first Action Results
 * hover, never per render or per pointer move.
 */

/** Screen facts the engine knows how to read; passed in rather than re-implemented. */
export interface ISymbolPickerHostEngine {
  /** A list component whose children repeat per row (Table, Gallery, Repeater…). */
  isRepeatingParent: (di: IDisplayItem) => boolean;
  /** The name the picker shows for a component. */
  getComponentLabel: (di: IDisplayItem) => string;
}

type SymbolPickerHostProps = ISymbolPickerCascaderProps & IWithSDProps;

/** A property's design-time text is shown only this long; the pane is narrow. */
const DESIGN_VALUE_MAX_LENGTH: number = 80;

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

function describeType(typeName: string | undefined, cardinality: ECardinality | undefined): string | undefined {
  if (!typeName) {
    return undefined;
  }
  const display: string = displaySymbolPickerTypeName(typeName);
  return cardinality === ECardinality.Many ? `List of ${display}` : display;
}

function findActionDefinition(props: SymbolPickerHostProps): IActionDefinitionDto | undefined {
  const definitionId: string | undefined = props.action?.actionDefinitionId;
  if (!definitionId) {
    return undefined;
  }
  return (props.sd.actionDefinitions ?? []).find(
    (definition: IActionDefinitionDto): boolean => definition.id === definitionId
  );
}

export function buildSymbolPickerBindingTarget(
  props: SymbolPickerHostProps,
  engine: ISymbolPickerHostEngine
): ISymbolPickerBindingTarget | undefined {
  // A condition's operands bind into the condition, not into a named field.
  if (props.isConditionPicker === true) {
    return undefined;
  }
  const typeLabel: string | undefined = describeType(props.targetTypeName, props.targetCardinality);
  // `parameter` is the generic action-parameter path; `bindingParameter` is the same fact for a
  // hand-built symbols group (Notify, Send Mail, …) that edits a named parameter's symbol directly
  // without wiring `parameter` itself — see SymbolPicker's `bindingParameter` doc for why the two
  // are not interchangeable at the option-generation layer.
  const parameterSource: IActionParameter | undefined = props.parameter ?? props.bindingParameter;
  if (parameterSource) {
    const label: IActionParameterLabel = getActionParameterLabel(parameterSource);
    return {
      name: props.bindingLabel ?? label.name,
      typeLabel,
      description: getActionParameterDoc(findActionDefinition(props), parameterSource.name),
      actionName: readString(props.action?.label)
    };
  }
  if (props.property?.name) {
    return {
      name: props.property.name,
      typeLabel,
      description: readString(props.property.description),
      componentName: props.targetDI ? engine.getComponentLabel(props.targetDI) : undefined
    };
  }
  return undefined;
}

function walkDisplayItems(items: IDisplayItem[] | undefined, visit: (di: IDisplayItem) => void): void {
  for (const di of items ?? []) {
    if (!di) {
      continue;
    }
    visit(di);
    walkDisplayItems(di.children, visit);
  }
}

/**
 * The ordered action lists the engine offers results from (DesignerSymbolPickerEngine.getAllActions,
 * same precedence): the one list of a document parameter / scheduled job / API method / BLE method /
 * workflow step, else every component event on the screen.
 */
export function collectSymbolPickerActionSequences(
  props: SymbolPickerHostProps,
  engine: ISymbolPickerHostEngine
): ISymbolPickerActionSequence[] {
  const sd: SymbolPickerHostProps["sd"] = props.sd;
  if (sd.isDocumentParameterAuthoringActive()) {
    return [{ eventName: "Document parameters", actions: sd.documentParameterEvent?.actions ?? [] }];
  }
  const scheduledJobActions: unknown = sd.getSelectedSj()?.json?.actions;
  if (Array.isArray(scheduledJobActions)) {
    return [{ eventName: "Scheduled job", actions: scheduledJobActions as IAction[] }];
  }
  const apiMethod: ReturnType<SymbolPickerHostProps["sd"]["getSelectedApiMethod"]> = sd.getSelectedApiMethod();
  if (sd.getSelectedApi() && apiMethod?.actions && sd.dashboardState === DashboardState.apiManagement) {
    return [{ eventName: "Method", actions: apiMethod.actions }];
  }
  if (sd.dashboardState === DashboardState.workFlow) {
    const stepActions: IAction[] | undefined = sd.getSelectedWfStep()?.actions;
    return stepActions ? [{ eventName: "Step", actions: stepActions }] : [];
  }
  const sequences: ISymbolPickerActionSequence[] = [];
  walkDisplayItems(sd.getScreenDesign()?.displayItems, (di: IDisplayItem): void => {
    const events: IEvent[] = di.item?.events ?? [];
    for (const event of events) {
      sequences.push({
        ownerName: engine.getComponentLabel(di),
        eventName: readString(event.eventName),
        actions: (event.actions ?? []).filter((action: IAction): boolean => Boolean(action))
      });
    }
  });
  return sequences;
}

function readDesignValue(di: IDisplayItem, propertyName: string): string | undefined {
  const property: IDisplayItemProperty | undefined = (di.item?.properties ?? []).find(
    (candidate: IDisplayItemProperty): boolean => candidate?.name === propertyName
  );
  if (!property) {
    return undefined;
  }
  const symbol: IDisplayItemProperty["symbol"] | undefined = property.symbol;
  // Bound to something else: its design-time value is not what the app will read.
  if (symbol && symbol.category !== ESymbolCategory.fixed && symbol.isFixed !== true) {
    return undefined;
  }
  const raw: unknown = symbol?.fixedValue ?? property.value;
  let text: string | undefined;
  if (typeof raw === "string") {
    text = raw.trim();
  } else if (typeof raw === "number" || typeof raw === "boolean") {
    text = String(raw);
  }
  if (!text) {
    return undefined;
  }
  return text.length > DESIGN_VALUE_MAX_LENGTH ? `${text.slice(0, DESIGN_VALUE_MAX_LENGTH - 1)}…` : text;
}

function findRepeatingParentName(
  di: IDisplayItem,
  props: SymbolPickerHostProps,
  engine: ISymbolPickerHostEngine
): string | undefined {
  const dictionary: SymbolPickerHostProps["sd"]["screenDesignDictionary"] = props.sd.screenDesignDictionary;
  if (!dictionary) {
    return undefined;
  }
  // Cycle-safe: a parent id that is missing or loops ends the walk.
  const visited: Set<IDisplayItem> = new Set<IDisplayItem>([di]);
  let parentId: number | undefined = di.parentId;
  while (parentId !== undefined && parentId !== null) {
    const parent: IDisplayItem | undefined = dictionary[parentId];
    if (!parent || visited.has(parent)) {
      return undefined;
    }
    if (engine.isRepeatingParent(parent)) {
      return engine.getComponentLabel(parent);
    }
    visited.add(parent);
    parentId = parent.parentId;
  }
  return undefined;
}

function isDisplayItem(value: unknown): value is IDisplayItem {
  return value !== null && typeof value === "object" && "item" in (value as IDisplayItem);
}

function resolveDisplayItem(option: CascaderOptionType, props: SymbolPickerHostProps): IDisplayItem | undefined {
  if (isDisplayItem(option.di)) {
    return option.di;
  }
  const diId: unknown = option.diId;
  if (typeof diId === "number") {
    return props.sd.screenDesignDictionary?.[diId];
  }
  return undefined;
}

export function describeSymbolPickerComponent(
  option: CascaderOptionType,
  props: SymbolPickerHostProps,
  engine: ISymbolPickerHostEngine
): ISymbolPickerComponentFacts | undefined {
  const di: IDisplayItem | undefined = resolveDisplayItem(option, props);
  if (!di) {
    return undefined;
  }
  const propertyName: string | undefined =
    option.category === ESymbolCategory.displayItemProp
      ? (readString(option.friendlyVariableName) ?? readString(option.label))
      : undefined;
  return {
    typeName: readString(di.item?.name),
    designValue: propertyName ? readDesignValue(di, propertyName) : undefined,
    repeatingParentName: findRepeatingParentName(di, props, engine)
  };
}

/** The whole context for one picker. */
export function buildSymbolPickerPreviewContext(
  props: SymbolPickerHostProps,
  engine: ISymbolPickerHostEngine
): ISymbolPickerPreviewContext {
  let sequences: ISymbolPickerActionSequence[] | undefined;
  const origins: Map<string, ISymbolPickerActionResultOrigin | undefined> = new Map<
    string,
    ISymbolPickerActionResultOrigin | undefined
  >();
  const hasHostType: boolean = Boolean(props.targetTypeName) && props.isObject !== true;
  return {
    target: buildSymbolPickerBindingTarget(props, engine),
    targetShape: hasHostType ? { typeName: props.targetTypeName, cardinality: props.targetCardinality } : undefined,
    describeActionResult: (resultActionId: string): ISymbolPickerActionResultOrigin | undefined => {
      if (!origins.has(resultActionId)) {
        sequences = sequences ?? collectSymbolPickerActionSequences(props, engine);
        origins.set(resultActionId, resolveSymbolPickerActionResultOrigin(sequences, resultActionId, props.action?.id));
      }
      return origins.get(resultActionId);
    },
    describeComponent: (option: CascaderOptionType): ISymbolPickerComponentFacts | undefined =>
      describeSymbolPickerComponent(option, props, engine)
  };
}
